/**
 * Generic API Client
 */
import { ApiResponse } from './types';
import { generateCacheKey, getCache, setCache } from './cache';

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public code?: string,
    public requestId?: string,
    /**
     * The raw parsed JSON response body, if any.
     * Preserved for endpoints that return domain-specific error envelopes
     * (e.g. quotaGuard returns `{ error, metric, resetAt }` with HTTP 402)
     * so callers can recover fields the generic envelope doesn't carry.
     */
    public body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface CacheConfig {
  enabled?: boolean; // 是否启用缓存，默认 false
  strategy?: 'cache-only' | 'cache-first' | 'network-first'; // 缓存策略，默认 'cache-only'
}

export interface ClientConfig {
  baseUrl: string;
  /**
   * Optional synchronous token getter retained for non-OIDC consumers.
   * OIDC-based apps should rely on {@link getAccessToken} and may omit this.
   */
  getToken?: () => string | null;
  getAccessToken?: () => Promise<string | null>;
  onUnauthorized?: () => void;
  onEmailNotVerified?: () => void;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  cache?: CacheConfig;
}

/**
 * 创建带超时的 fetch 请求
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout: number
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, '请求超时，请稍后重试', 'REQUEST_TIMEOUT');
    }
    throw error;
  }
}

/**
 * 安全解析 JSON 响应
 */
async function safeJsonParse<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type');

  // 检查响应类型
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      `服务器返回了非 JSON 格式的响应: ${text.substring(0, 100)}`,
      'INVALID_RESPONSE_FORMAT'
    );
  }

  try {
    const text = await response.text();
    if (!text) {
      // 空响应
      return { success: false, error: { message: '服务器返回了空响应' } } as ApiResponse<T>;
    }
    return JSON.parse(text) as ApiResponse<T>;
  } catch {
    throw new ApiError(response.status, '无法解析服务器响应', 'JSON_PARSE_ERROR');
  }
}

/**
 * 判断错误是否可重试
 */
function isRetryableError(error: ApiError): boolean {
  // 网络错误、超时、5xx 服务器错误可以重试
  return (
    error.code === 'NETWORK_ERROR' ||
    error.code === 'REQUEST_TIMEOUT' ||
    (error.statusCode >= 500 && error.statusCode < 600)
  );
}

/**
 * 延迟函数
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isPublicAuthEndpoint(endpoint: string): boolean {
  return endpoint.startsWith('/api/auth/me');
}

/** 扩展 RequestInit，支持单次请求超时（毫秒） */
export type RequestOptions = RequestInit & { timeout?: number };

export async function createRequest<T>(
  endpoint: string,
  config: ClientConfig,
  options: RequestOptions = {}
): Promise<T> {
  const url = `${config.baseUrl}${endpoint}`;
  let token = config.getToken?.() ?? null;
  if (!token && config.getAccessToken) {
    token = await config.getAccessToken();
  }
  const requestTimeout = (
    typeof (options as RequestOptions).timeout === 'number'
      ? (options as RequestOptions).timeout
      : (config.timeout ?? 30000)
  ) as number; // 默认 30 秒
  const maxRetries = config.retries || 0;
  const retryDelay = config.retryDelay || 1000;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const publicAuthEndpoint = isPublicAuthEndpoint(endpoint);

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  } else if (!publicAuthEndpoint) {
    console.warn(`[API] No token available for request to ${endpoint}`);
  }

  const { timeout: _omit, ...fetchOptions } = options;
  let lastError: ApiError | null = null;

  // 重试逻辑
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // 如果不是第一次尝试，等待一段时间
      if (attempt > 0) {
        await delay(retryDelay * attempt); // 指数退避
      }

      let response: Response;
      try {
        response = await fetchWithTimeout(
          url,
          { credentials: 'include', ...fetchOptions, headers },
          requestTimeout
        );
      } catch (error) {
        // 网络错误处理
        if (error instanceof ApiError) {
          lastError = error;
          if (isRetryableError(error) && attempt < maxRetries) {
            console.warn(
              `[API] Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying...`,
              error.message
            );
            continue;
          }
          throw error;
        }

        // 其他网络错误（CORS、连接失败等）
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
          lastError = new ApiError(0, '网络连接失败，请检查网络连接', 'NETWORK_ERROR');
          if (attempt < maxRetries) {
            console.warn(
              `[API] Network error (attempt ${attempt + 1}/${maxRetries + 1}), retrying...`
            );
            continue;
          }
          throw lastError;
        }

        throw new ApiError(0, `网络错误: ${errorMessage}`, 'NETWORK_ERROR');
      }

      // Handle No Content
      if (response.status === 204) {
        return {} as T;
      }

      // 安全解析 JSON
      const data = await safeJsonParse<T>(response);

      if (!response.ok || !data.success) {
        const apiError = new ApiError(
          response.status,
          data.error?.message || 'Request failed',
          data.error?.code,
          (data.error as any)?.requestId,
          data
        );

        if (response.status === 401 && !publicAuthEndpoint) {
          console.warn(`[API] Unauthorized (401) for ${endpoint}`);
          config.onUnauthorized?.();
        }

        if (response.status === 403 && data.error?.code === 'EMAIL_NOT_VERIFIED') {
          config.onEmailNotVerified?.();
        }

        // 5xx 错误可以重试
        if (isRetryableError(apiError) && attempt < maxRetries) {
          lastError = apiError;
          console.warn(
            `[API] Server error (attempt ${attempt + 1}/${maxRetries + 1}), retrying...`,
            apiError.message
          );
          continue;
        }

        throw apiError;
      }

      return data.data as T;
    } catch (error) {
      if (error instanceof ApiError) {
        // 如果是最后一次尝试，或者错误不可重试，直接抛出
        if (attempt >= maxRetries || !isRetryableError(error)) {
          throw error;
        }
        lastError = error;
      } else {
        // 未知错误，不重试
        throw new ApiError(
          0,
          `未知错误: ${error instanceof Error ? error.message : String(error)}`,
          'UNKNOWN_ERROR'
        );
      }
    }
  }

  // 所有重试都失败了
  if (lastError) {
    throw lastError;
  }

  throw new ApiError(0, '请求失败', 'REQUEST_FAILED');
}

/**
 * Parse `Content-Disposition` filename for authenticated file downloads.
 * Supports `filename="…"` and `filename*=UTF-8''…`.
 */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }
  const plainMatch = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
  return plainMatch?.[1]?.trim() ?? null;
}

export interface TextDownloadResult {
  text: string;
  filename: string;
}

/**
 * Authenticated text/file download. Unlike {@link createRequest}, this does not
 * assume a JSON success envelope — used for admin CSV exports that return a
 * raw body with `Content-Disposition`.
 */
export async function createTextDownload(
  endpoint: string,
  config: ClientConfig,
  options: RequestOptions = {},
  fallbackFilename = 'download.txt'
): Promise<TextDownloadResult> {
  const url = `${config.baseUrl}${endpoint}`;
  let token = config.getToken?.() ?? null;
  if (!token && config.getAccessToken) {
    token = await config.getAccessToken();
  }

  const requestTimeout = (
    typeof options.timeout === 'number' ? options.timeout : (config.timeout ?? 30000)
  ) as number;

  const headers: HeadersInit = {
    ...options.headers,
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn(`[API] No token available for download to ${endpoint}`);
  }

  const { timeout: _omit, ...fetchOptions } = options;
  const response = await fetchWithTimeout(
    url,
    { credentials: 'include', ...fetchOptions, headers },
    requestTimeout
  );

  const text = await response.text();

  if (!response.ok) {
    let message = 'Download failed';
    let code: string | undefined;
    if (text) {
      try {
        const data = JSON.parse(text) as {
          error?: { message?: string; code?: string };
          success?: boolean;
        };
        message = data.error?.message || message;
        code = data.error?.code;
      } catch {
        message = text.slice(0, 200) || message;
      }
    }
    if (response.status === 401) {
      config.onUnauthorized?.();
    }
    throw new ApiError(response.status, message, code);
  }

  const filename =
    parseContentDispositionFilename(response.headers.get('Content-Disposition')) ||
    fallbackFilename;

  return { text, filename };
}

/**
 * 创建带缓存的请求函数（cache-only 策略）
 * 先查缓存，有则直接返回；无缓存则请求网络，成功后保存到缓存
 */
export async function createCachedRequest<T>(
  endpoint: string,
  config: ClientConfig,
  options: RequestInit = {}
): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const cacheConfig = config.cache || {};
  const enabled = cacheConfig.enabled ?? false;
  const strategy = cacheConfig.strategy || 'cache-only';

  // 如果未启用缓存，直接使用普通请求
  if (!enabled) {
    return createRequest<T>(endpoint, config, options);
  }

  // 只对 GET 请求启用缓存
  if (method !== 'GET') {
    return createRequest<T>(endpoint, config, options);
  }

  // 生成缓存键
  const queryString = endpoint.includes('?')
    ? endpoint.split('?')[1].split('&').sort().join('&')
    : '';
  const cacheKey = generateCacheKey(method, endpoint.split('?')[0], queryString);

  // cache-only 策略：先查缓存，有则返回；无则请求网络
  if (strategy === 'cache-only') {
    // 先尝试从缓存读取
    const cachedData = await getCache<T>(cacheKey);
    if (cachedData !== null) {
      console.log(`[Cache] Hit cache for ${endpoint}`);
      return cachedData;
    }

    // 缓存未命中，请求网络
    console.log(`[Cache] Miss cache for ${endpoint}, fetching from network...`);
    try {
      const data = await createRequest<T>(endpoint, config, options);
      // 请求成功，保存到缓存
      await setCache(cacheKey, data);
      return data;
    } catch (error) {
      // 请求失败，抛出错误（cache-only 策略不返回缓存）
      throw error;
    }
  }

  // 其他策略暂不支持，降级为普通请求
  return createRequest<T>(endpoint, config, options);
}
